import React from "react";
import LookbackToggle from "./LookbackToggle";
import {
  buildRoleTypeNameMap,
  getLookbackRange,
  getMemberRoleTypes,
  isActiveEmployee,
  isDateWithinRange,
  registerRoleTypeName,
  safeLocaleCompare,
} from "./utils";

const DEFAULT_BASELINE_HOURS_MIN = 20;
const DEFAULT_BASELINE_HOURS_MAX = 40;
const PEAK_BUFFER_MULTIPLIER = 1.25;
const HEADCOUNT_GAP_TOLERANCE = 0.25;

function formatNumber(value, digits = 1) {
  if (
    value === null ||
    value === undefined ||
    Number.isNaN(value) ||
    !Number.isFinite(Number(value))
  ) {
    return "—";
  }
  return Number(value).toFixed(digits);
}

function sanitizeRange(lower, upper) {
  const lowerVal = Number.isFinite(lower) ? Math.max(0, lower) : null;
  const upperVal = Number.isFinite(upper) ? Math.max(0, upper) : null;

  if (lowerVal === null && upperVal === null) {
    return null;
  }

  if (lowerVal === null) {
    return { lower: upperVal, upper: upperVal };
  }

  if (upperVal === null) {
    return { lower: lowerVal, upper: lowerVal };
  }

  const minVal = Math.min(lowerVal, upperVal);
  const maxVal = Math.max(lowerVal, upperVal);
  return { lower: minVal, upper: maxVal };
}

function combineRanges(...ranges) {
  const lowerCandidates = [];
  const upperCandidates = [];

  ranges.forEach((range) => {
    if (!range) {
      return;
    }
    if (Number.isFinite(range.lower)) {
      lowerCandidates.push(Math.max(0, range.lower));
    }
    if (Number.isFinite(range.upper)) {
      upperCandidates.push(Math.max(0, range.upper));
    }
  });

  const positiveLower = lowerCandidates.filter((value) => value > 0);
  const positiveUpper = upperCandidates.filter((value) => value > 0);

  const effectiveLower = positiveLower.length
    ? positiveLower
    : lowerCandidates.length
    ? lowerCandidates
    : [];
  const effectiveUpper = positiveUpper.length
    ? positiveUpper
    : upperCandidates.length
    ? upperCandidates
    : [];

  if (effectiveLower.length === 0 && effectiveUpper.length === 0) {
    return sanitizeRange(0, 0);
  }

  const lower = effectiveLower.length
    ? Math.min(...effectiveLower)
    : Math.min(...effectiveUpper);
  const upper = effectiveUpper.length
    ? Math.max(...effectiveUpper)
    : Math.max(...effectiveLower);

  return sanitizeRange(lower, upper);
}

function computeHourBenchmarkRange(avgPreferred, avgActualPerEmployee) {
  const hasTarget = Number.isFinite(avgPreferred) && avgPreferred > 0;
  const hasActual =
    Number.isFinite(avgActualPerEmployee) && avgActualPerEmployee > 0;

  const lowerBenchmark = hasTarget
    ? Math.max(avgPreferred, DEFAULT_BASELINE_HOURS_MIN)
    : hasActual
    ? Math.max(avgActualPerEmployee, DEFAULT_BASELINE_HOURS_MIN)
    : DEFAULT_BASELINE_HOURS_MIN;

  const upperSource = hasActual
    ? Math.max(avgActualPerEmployee, DEFAULT_BASELINE_HOURS_MIN)
    : hasTarget
    ? Math.max(avgPreferred, DEFAULT_BASELINE_HOURS_MAX)
    : DEFAULT_BASELINE_HOURS_MAX;

  return sanitizeRange(lowerBenchmark, upperSource);
}

function computeHoursDrivenHeadcountRange(weeklyForecastHours, benchmarkRange) {
  if (!(weeklyForecastHours > 0) || !benchmarkRange) {
    return sanitizeRange(0, 0);
  }

  const { lower, upper } = benchmarkRange;
  const lowerBound = upper > 0 ? weeklyForecastHours / upper : 0;
  const upperBound = lower > 0 ? weeklyForecastHours / lower : lowerBound;
  return sanitizeRange(lowerBound, upperBound);
}

function computePeakHeadcountRange(averagePeak, maxPeak) {
  const avgRequirement = Math.max(0, averagePeak || 0) * PEAK_BUFFER_MULTIPLIER;
  const maxRequirement = Math.max(0, maxPeak || 0) * PEAK_BUFFER_MULTIPLIER;
  return sanitizeRange(avgRequirement, maxRequirement);
}

function formatHeadcount(value) {
  if (value && typeof value === "object") {
    const normalized = sanitizeRange(value.lower, value.upper);
    if (!normalized) {
      return "—";
    }
    const roundedLower = Math.round(normalized.lower);
    const roundedUpper = Math.round(normalized.upper);
    if (roundedLower === roundedUpper) {
      return String(roundedUpper);
    }
    return `${roundedLower}–${roundedUpper}`;
  }

  if (!Number.isFinite(Number(value))) {
    return "—";
  }
  return String(Math.round(Number(value)));
}

function buildStaffingAction(eligibleCount, recommendedRange) {
  if (!Number.isFinite(eligibleCount)) {
    return { label: "—", tone: "neutral" };
  }

  const normalized = sanitizeRange(
    recommendedRange?.lower,
    recommendedRange?.upper
  );

  if (!normalized) {
    return { label: "—", tone: "neutral" };
  }

  const { lower, upper } = normalized;

  if (eligibleCount < lower - HEADCOUNT_GAP_TOLERANCE) {
    const hireCount = Math.max(1, Math.ceil(lower - eligibleCount));
    return {
      label: `Hire ${hireCount}`,
      tone: "negative",
    };
  }

  if (eligibleCount > upper + HEADCOUNT_GAP_TOLERANCE) {
    const surplusCount = Math.max(1, Math.ceil(eligibleCount - upper));
    return {
      label: `Surplus ${surplusCount}`,
      tone: "positive",
    };
  }

  return { label: "On track", tone: "neutral" };
}

function calculateHeadcountDelta(eligibleCount, recommendedRange) {
  if (!Number.isFinite(eligibleCount)) {
    return 0;
  }
  const normalized = sanitizeRange(
    recommendedRange?.lower,
    recommendedRange?.upper
  );
  if (!normalized) {
    return 0;
  }
  if (eligibleCount < normalized.lower) {
    return eligibleCount - normalized.lower;
  }
  if (eligibleCount > normalized.upper) {
    return eligibleCount - normalized.upper;
  }
  return 0;
}

function staffingToneClass(tone) {
  if (tone === "negative") {
    return "text-rose-600 font-semibold";
  }
  if (tone === "positive") {
    return "text-emerald-600 font-semibold";
  }
  return "text-slate-500";
}

function formatDateRange(range) {
  if (!range?.start || !range?.end) {
    return "";
  }
  const start = range.start.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  const end = range.end.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return `${start} – ${end}`;
}

function buildRoleTypeSet(planningData, roleTypeNameMap) {
  const roleTypeSet = new Set();
  const addRoleType = (value) => {
    const resolved = registerRoleTypeName(value, roleTypeNameMap);
    if (resolved) {
      roleTypeSet.add(resolved);
    }
  };

  planningData.roleTypes?.forEach((role) => {
    addRoleType(role);
    if (role?.role_type) {
      addRoleType(role.role_type);
    }
  });
  planningData.membersById?.forEach((member) => {
    getMemberRoleTypes(member, roleTypeNameMap).forEach((roleType) => {
      addRoleType(roleType);
    });
  });
  planningData.spentLabor?.forEach((entry) => {
    addRoleType(entry?.role_type);
  });
  planningData.forecastHistory?.forEach((entry) => {
    addRoleType(entry?.role_type);
  });
  return Array.from(roleTypeSet).sort((a, b) => safeLocaleCompare(a, b));
}

function HeadcountPlanningOverview({
  planningData,
  lookbackWeeks,
  onLookbackChange,
  lookbackOptions,
}) {
  const { historyRange } = planningData ?? {};
  const lookbackRange = React.useMemo(
    () => getLookbackRange(historyRange, lookbackWeeks),
    [historyRange, lookbackWeeks]
  );
  const rangeDescription = React.useMemo(
    () => formatDateRange(lookbackRange),
    [lookbackRange]
  );
  const calculationDetails = React.useMemo(
    () => [
      {
        title: "Eligible headcount",
        description:
          "Counts active, non-hidden employees mapped to the department.",
      },
      {
        title: "Average hours",
        description: `Per-employee weekly averages across the selected ${lookbackWeeks} completed weeks.`,
      },
      {
        title: "Recommended headcount range",
        description:
          "Blends an hours-based estimate (preferring target and actual weekly hours with a 20–40 hour fallback) with peak coverage that adds a 25% buffer above the average and max daily peaks. The displayed range spans the tightest lower and highest upper bounds from those signals.",
      },
    ],
    [lookbackWeeks]
  );

  const rows = React.useMemo(() => {
    if (!planningData) {
      return { departments: [], totals: null };
    }

    const members = Array.from(planningData.membersById?.values() || []);
    const activeMembers = members.filter((member) => isActiveEmployee(member));
    const roleTypeNameMap =
      planningData.roleTypeNameMap ||
      buildRoleTypeNameMap(planningData.roleTypes);
    const roleTypes = buildRoleTypeSet(planningData, roleTypeNameMap);

    const actualHoursByRole = new Map();
    const spentLaborEntries = (planningData.spentLabor || []).filter((entry) =>
      isDateWithinRange(entry?.date, lookbackRange)
    );
    spentLaborEntries.forEach((entry) => {
      const roleType = registerRoleTypeName(entry?.role_type, roleTypeNameMap);
      const hours = Number(entry?.hours || 0);
      if (!roleType || Number.isNaN(hours)) {
        return;
      }
      actualHoursByRole.set(
        roleType,
        (actualHoursByRole.get(roleType) || 0) + hours
      );
    });

    const forecastHoursByRole = new Map();
    const roleDailyPeakLabor = new Map();
    const peakLaborByTimeslot = new Map();
    const forecastHistoryEntries = (planningData.forecastHistory || []).filter(
      (entry) => isDateWithinRange(entry?.date, lookbackRange)
    );
    forecastHistoryEntries.forEach((entry) => {
      const roleType = registerRoleTypeName(entry?.role_type, roleTypeNameMap);
      const hours = Number(entry?.hours || 0);
      const labor = Number(entry?.labor);
      if (!roleType || Number.isNaN(hours)) {
        if (!Number.isFinite(labor)) {
          return;
        }
      }
      if (roleType && !Number.isNaN(hours)) {
        forecastHoursByRole.set(
          roleType,
          (forecastHoursByRole.get(roleType) || 0) + hours
        );
      }
      if (roleType && Number.isFinite(labor)) {
        const positiveLabor = Math.max(labor, 0);
        if (entry?.date) {
          let roleDatePeaks = roleDailyPeakLabor.get(roleType);
          if (!roleDatePeaks) {
            roleDatePeaks = new Map();
            roleDailyPeakLabor.set(roleType, roleDatePeaks);
          }
          const currentPeak = roleDatePeaks.get(entry.date) || 0;
          if (positiveLabor > currentPeak) {
            roleDatePeaks.set(entry.date, positiveLabor);
          }
        }
      }
      if (Number.isFinite(labor)) {
        const positiveLabor = Math.max(labor, 0);
        const slotKey = `${entry?.date || ""}_${entry?.qh_num ?? ""}`;
        const existing = peakLaborByTimeslot.get(slotKey) || 0;
        const updated = existing + positiveLabor;
        if (updated > existing) {
          peakLaborByTimeslot.set(slotKey, updated);
        }
      }
    });

    const averagePeakLaborByRole = new Map();
    const maxPeakLaborByRole = new Map();
    roleDailyPeakLabor.forEach((dateMap, roleType) => {
      const peaks = Array.from(dateMap.values());
      if (peaks.length) {
        const averagePeak =
          peaks.reduce((sum, value) => sum + value, 0) / peaks.length;
        averagePeakLaborByRole.set(roleType, averagePeak);
        maxPeakLaborByRole.set(roleType, Math.max(...peaks));
      } else {
        averagePeakLaborByRole.set(roleType, 0);
        maxPeakLaborByRole.set(roleType, 0);
      }
    });

    const maxLaborByDate = new Map();
    peakLaborByTimeslot.forEach((value, key) => {
      const separatorIndex = key.indexOf("_");
      const dateKey = separatorIndex >= 0 ? key.slice(0, separatorIndex) : key;
      const currentMax = maxLaborByDate.get(dateKey) || 0;
      if (value > currentMax) {
        maxLaborByDate.set(dateKey, value);
      }
    });
    const dailyMaxValues = Array.from(maxLaborByDate.values());
    const averagePeakLaborAll = dailyMaxValues.length
      ? dailyMaxValues.reduce((sum, value) => sum + value, 0) /
        dailyMaxValues.length
      : 0;
    const maxPeakLaborAll = dailyMaxValues.length
      ? Math.max(...dailyMaxValues)
      : 0;

    const departmentRows = roleTypes.map((roleType) => {
      const eligibleMembers = activeMembers.filter((member) => {
        const primaryRole =
          registerRoleTypeName(
            member?.primary_role_type_name,
            roleTypeNameMap
          ) ||
          registerRoleTypeName(member?.primary_role_type, roleTypeNameMap);
        return primaryRole === roleType;
      });
      const eligibleCount = eligibleMembers.length;
      const preferredValues = eligibleMembers
        .map((member) => Number(member?.target_weekly_hours || 0))
        .filter((value) => value > 0);
      const preferredCount = preferredValues.length;
      const avgPreferred = preferredCount
        ? preferredValues.reduce((sum, value) => sum + value, 0) /
          preferredCount
        : 0;

      const totalActualHours = actualHoursByRole.get(roleType) || 0;
      const weeklyActualHours =
        totalActualHours / Math.max(lookbackWeeks || 0, 1);
      const avgActualPerEmployee = eligibleCount
        ? weeklyActualHours / eligibleCount
        : 0;

      const totalForecastHours = forecastHoursByRole.get(roleType) || 0;
      const weeklyForecastHours =
        totalForecastHours / Math.max(lookbackWeeks || 0, 1);
      const avgForecastPerEmployee = eligibleCount
        ? weeklyForecastHours / eligibleCount
        : 0;

      const hourBenchmarkRange = computeHourBenchmarkRange(
        avgPreferred,
        avgActualPerEmployee
      );
      const hoursDrivenRange = computeHoursDrivenHeadcountRange(
        weeklyForecastHours,
        hourBenchmarkRange
      );
      const averagePeak = averagePeakLaborByRole.get(roleType) || 0;
      const maxPeak = maxPeakLaborByRole.get(roleType) || averagePeak;
      const peakRange = computePeakHeadcountRange(averagePeak, maxPeak);
      const recommendedHeadcountRange = combineRanges(
        hoursDrivenRange,
        peakRange
      );
      const deltaFromRecommended = calculateHeadcountDelta(
        eligibleCount,
        recommendedHeadcountRange
      );
      const staffingAction = buildStaffingAction(
        eligibleCount,
        recommendedHeadcountRange
      );

      return {
        roleType,
        eligibleCount,
        preferredCount,
        avgPreferred,
        avgActualPerEmployee,
        avgForecastPerEmployee,
        recommendedHeadcountRange,
        deltaFromRecommended,
        staffingAction,
      };
    });

    const totalActualHoursAll = Array.from(actualHoursByRole.values()).reduce(
      (sum, hours) => sum + hours,
      0
    );
    const totalForecastHoursAll = Array.from(
      forecastHoursByRole.values()
    ).reduce((sum, hours) => sum + hours, 0);
    const totalEligible = activeMembers.length;
    const preferredAll = activeMembers
      .map((member) => Number(member?.target_weekly_hours || 0))
      .filter((value) => value > 0);
    const preferredAllCount = preferredAll.length;
    const avgPreferredAll = preferredAllCount
      ? preferredAll.reduce((sum, value) => sum + value, 0) /
        preferredAllCount
      : 0;
    const denominatorWeeks = Math.max(lookbackWeeks || 0, 1);
    const weeklyActualAll = totalActualHoursAll / denominatorWeeks;
    const weeklyForecastAll = totalForecastHoursAll / denominatorWeeks;
    const avgActualAll = totalEligible
      ? weeklyActualAll / totalEligible
      : 0;
    const avgForecastAll = totalEligible
      ? weeklyForecastAll / totalEligible
      : 0;
    const baselineRangeAll = computeHourBenchmarkRange(
      avgPreferredAll,
      avgActualAll
    );
    const hoursDrivenRangeAll = computeHoursDrivenHeadcountRange(
      weeklyForecastAll,
      baselineRangeAll
    );
    const peakRangeAll = computePeakHeadcountRange(
      averagePeakLaborAll,
      maxPeakLaborAll
    );
    const recommendedRangeAll = combineRanges(
      hoursDrivenRangeAll,
      peakRangeAll
    );
    const deltaAll = calculateHeadcountDelta(
      totalEligible,
      recommendedRangeAll
    );
    const totalsStaffingAction = buildStaffingAction(
      totalEligible,
      recommendedRangeAll
    );

    const totals = {
      roleType: "All departments",
      eligibleCount: totalEligible,
      preferredCount: preferredAllCount,
      avgPreferred: avgPreferredAll,
      avgActualPerEmployee: avgActualAll,
      avgForecastPerEmployee: avgForecastAll,
      recommendedHeadcountRange: recommendedRangeAll,
      deltaFromRecommended: deltaAll,
      staffingAction: totalsStaffingAction,
    };

    return { departments: departmentRows, totals };
  }, [planningData, lookbackRange, lookbackWeeks]);

  return (
    <div className="space-y-6">
      <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <div className="flex flex-row items-center justify-between gap-3 mb-4 md:flex-col md:items-start">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              Last {lookbackWeeks}-week department snapshot
            </h3>
            <p className="text-sm text-slate-600">
              {false // previously rangeDescription
                ? `Aggregated across ${lookbackWeeks} completed weeks (${rangeDescription})`
                : `Aggregated across the last ${lookbackWeeks} completed weeks.`}
            </p>
          </div>
          <LookbackToggle
            value={lookbackWeeks}
            options={lookbackOptions}
            onChange={onLookbackChange}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="py-2 pr-4 font-medium">Department</th>
                <th className="py-2 pr-4 font-medium">Current Headcount</th>
                <th className="py-2 pr-4 font-medium">Optimal Headcount</th>
                <th className="py-2 pr-4 font-medium">Staffing guidance</th>
                <th className="py-2 pr-4 font-medium">Avg target hrs</th>
                <th className="py-2 pr-4 font-medium">Avg actual hrs</th>
                <th className="py-2 pr-4 font-medium">Avg forecast hrs</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {rows.departments.map((dept) => (
                <tr key={dept.roleType} className="bg-white">
                  <td className="py-3 pr-4 font-medium text-slate-900">
                    {dept.roleType}
                  </td>
                  <td className="py-3 pr-4 text-slate-700">
                    {formatHeadcount(dept.eligibleCount)}
                  </td>
                  <td className="py-3 pr-4 text-slate-700">
                    {formatHeadcount(dept.recommendedHeadcountRange)}
                  </td>
                  <td
                    className={`py-3 pr-4 ${staffingToneClass(
                      dept.staffingAction?.tone
                    )}`}
                  >
                    {dept.staffingAction?.label || "—"}
                  </td>
                  <td className="py-3 pr-4 text-slate-700">
                    {formatNumber(dept.avgPreferred)}
                  </td>
                  <td className="py-3 pr-4 text-slate-700">
                    {formatNumber(dept.avgActualPerEmployee)}
                  </td>
                  <td className="py-3 pr-4 text-slate-700">
                    {formatNumber(dept.avgForecastPerEmployee)}
                  </td>
                </tr>
              ))}
              {rows.totals && (
                <tr className="bg-violet-50 font-semibold text-violet-800">
                  <td className="py-3 pr-4">{rows.totals.roleType}</td>
                  <td className="py-3 pr-4">
                    {formatHeadcount(rows.totals.eligibleCount)}
                  </td>
                  <td className="py-3 pr-4">
                    {formatHeadcount(rows.totals.recommendedHeadcountRange)}
                  </td>
                  <td
                    className={`py-3 pr-4 ${staffingToneClass(
                      rows.totals.staffingAction?.tone
                    )}`}
                  >
                    {rows.totals.staffingAction?.label || "—"}
                  </td>
                  <td className="py-3 pr-4">
                    {formatNumber(rows.totals.avgPreferred)}
                  </td>
                  <td className="py-3 pr-4">
                    {formatNumber(rows.totals.avgActualPerEmployee)}
                  </td>
                  <td className="py-3 pr-4">
                    {formatNumber(rows.totals.avgForecastPerEmployee)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <h4 className="text-base font-semibold text-slate-900">
            How this is calculated
          </h4>
          <span className="text-xs font-semibold tracking-wide uppercase text-slate-400">
            Transparent assumptions
          </span>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {calculationDetails.map((item) => (
            <div
              key={item.title}
              className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                {item.title}
              </p>
              <p className="text-sm text-slate-700 leading-relaxed">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default HeadcountPlanningOverview;
